import { useMemo, useState } from "react";
import { useDeliverables } from "@/hooks/useLive";
import type { Category, Deliverable } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/Card";
import { CATEGORIES, STATUS_MAP } from "@/data/categories";
import { parseLaunchDate } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

interface DayTask {
  task: Deliverable;
  date: Date;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function CalendarPanel() {
  const tasks = useDeliverables();
  const today = new Date();
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(today));
  const [categoryFilter, setCategoryFilter] = useState<"all" | Category>("all");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Parse every task with a parseable due into a (task, date) pair, scoped to year ±1
  const allDayTasks = useMemo<DayTask[]>(() => {
    const out: DayTask[] = [];
    const year = cursor.getFullYear();
    for (const t of tasks) {
      const d = parseLaunchDate(String(t.due ?? ""), year);
      if (!d) continue;
      out.push({ task: t, date: d });
    }
    return out;
  }, [tasks, cursor]);

  const filtered = useMemo(() => {
    return allDayTasks.filter(({ task }) => {
      if (categoryFilter !== "all" && task.category !== categoryFilter) return false;
      if (ownerFilter && !String(task.owner ?? "").toLowerCase().includes(ownerFilter.toLowerCase())) return false;
      return true;
    });
  }, [allDayTasks, categoryFilter, ownerFilter]);

  // Build a map of day → tasks for the visible month
  const tasksByDay = useMemo(() => {
    const m = new Map<string, DayTask[]>();
    for (const dt of filtered) {
      if (dt.date.getFullYear() !== cursor.getFullYear() || dt.date.getMonth() !== cursor.getMonth()) continue;
      const key = dayKey(dt.date);
      const arr = m.get(key) ?? [];
      arr.push(dt);
      m.set(key, arr);
    }
    return m;
  }, [filtered, cursor]);

  // Build the grid: leading blanks + days of month + trailing blanks
  const grid = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const leading = start.getDay();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= end.getDate(); d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const owners = useMemo(() => {
    const s = new Set<string>();
    tasks.forEach((t) => { if (t.owner) s.add(String(t.owner).split(/[,/]/)[0].trim()); });
    return Array.from(s).sort();
  }, [tasks]);

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const visibleCount = filtered.filter((dt) => dt.date.getFullYear() === cursor.getFullYear() && dt.date.getMonth() === cursor.getMonth()).length;
  const skippedCount = tasks.length - allDayTasks.length;

  const selectedTasks = selectedDay ? (tasksByDay.get(selectedDay) ?? []) : [];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
              className="rounded-md border border-input bg-card p-1.5 text-muted-foreground hover:text-foreground"
              title="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => setCursor(startOfMonth(new Date()))}
              className="rounded-md border border-input bg-card px-2.5 py-1 text-caption font-medium text-muted-foreground hover:text-foreground"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
              className="rounded-md border border-input bg-card p-1.5 text-muted-foreground hover:text-foreground"
              title="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="text-h3 font-semibold text-foreground">{monthLabel}</div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as typeof categoryFilter)}
              className="h-8 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none"
            >
              <option value="all">All categories</option>
              {(Object.keys(CATEGORIES) as Category[]).map((c) => (
                <option key={c} value={c}>{CATEGORIES[c].label}</option>
              ))}
            </select>
            <input
              list="cal-owners"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              placeholder="Owner"
              className="h-8 w-32 rounded-md border border-input bg-card px-2 text-caption focus:border-primary focus:outline-none"
            />
            <datalist id="cal-owners">
              {owners.map((o) => <option key={o} value={o} />)}
            </datalist>
            <span className="text-micro text-muted-foreground">
              {visibleCount} this month{skippedCount > 0 ? ` · ${skippedCount} undated` : ""}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-7 border-b border-border bg-muted/30 text-center text-micro font-medium uppercase tracking-wider text-muted-foreground">
            {WEEKDAYS.map((w) => (
              <div key={w} className="py-2">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {grid.map((day, i) => {
              if (!day) {
                return <div key={`blank-${i}`} className="min-h-[112px] border-b border-r border-border bg-muted/10" />;
              }
              const key = dayKey(day);
              const dayTasks = tasksByDay.get(key) ?? [];
              const isToday = sameDay(day, today);
              const isSelected = selectedDay === key;
              const visible = dayTasks.slice(0, 3);
              const overflow = dayTasks.length - visible.length;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDay(isSelected ? null : key)}
                  className={cn(
                    "flex min-h-[112px] flex-col items-stretch gap-1 border-b border-r border-border p-1.5 text-left transition-colors hover:bg-accent/40",
                    isSelected && "bg-primary/5",
                    (i + 1) % 7 === 0 && "border-r-0"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className={cn(
                      "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-caption font-medium",
                      isToday ? "bg-primary text-white" : "text-foreground"
                    )}>
                      {day.getDate()}
                    </span>
                    {dayTasks.length > 0 && !isToday && (
                      <span className="text-micro text-muted-foreground">{dayTasks.length}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {visible.map(({ task }) => {
                      const cat = CATEGORIES[task.category];
                      return (
                        <div
                          key={task.id}
                          className="flex items-center gap-1 truncate rounded px-1 py-0.5 text-micro"
                          style={{ backgroundColor: cat ? `${cat.color}1a` : undefined, color: cat?.color }}
                          title={`${task.name} — ${task.owner}`}
                        >
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: cat?.color }} />
                          <span className="truncate">{task.name}</span>
                        </div>
                      );
                    })}
                    {overflow > 0 && (
                      <div className="px-1 text-micro text-muted-foreground">+ {overflow} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {selectedDay && selectedTasks.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="mb-3 flex items-center gap-2 text-caption text-muted-foreground">
              <CalendarIcon size={14} />
              <span className="font-medium text-foreground">
                {(() => {
                  const [y, m, d] = selectedDay.split("-").map(Number);
                  return new Date(y, m, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
                })()}
              </span>
              <span>· {selectedTasks.length} task{selectedTasks.length === 1 ? "" : "s"}</span>
            </div>
            <ul className="space-y-2">
              {selectedTasks.map(({ task }) => {
                const cat = CATEGORIES[task.category];
                const status = STATUS_MAP[task.status];
                return (
                  <li key={task.id} className="flex items-start gap-3 rounded-md border border-border bg-card p-3">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: cat?.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-body font-medium text-foreground">{task.name}</div>
                      <div className="mt-0.5 text-micro text-muted-foreground">
                        {cat?.label ?? task.category} · {task.owner || "—"} · {task.phase || "—"}
                      </div>
                    </div>
                    {status && (
                      <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-micro font-medium", status.bg, status.color)}>
                        {status.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {skippedCount > 0 && (
        <p className="text-micro text-muted-foreground">
          {skippedCount} task{skippedCount === 1 ? "" : "s"} not shown — their <code className="rounded bg-muted px-1">due</code> field is empty or doesn't include a parseable date (e.g. "Late May", "Ongoing"). Edit the task to set a concrete date and it'll appear on the calendar.
        </p>
      )}
    </div>
  );
}
